import React, { useEffect, useMemo, useState } from 'react';
import { approvalPolicies, systemRoles as rolesApi } from '../../api/endpoints';
import type { ApprovalPolicyView } from '../../api/endpoints';
import type { SystemRole } from '../../types';
import { Button } from '../../components/ui';
import type { Tokens } from '../../theme/tokens';

// Settings › Approvals, arranged the way people think about it: pick a part
// of the app, then for each thing that can happen there (adding, editing,
// deleting, and a few special ones) switch approval on or off and list the
// approvers in order. A sentence under each says exactly what will happen.
//
// Underneath it is the same data as before: one policy per type
// (REGISTRATION, BUDGET, BUDGET_ADJUSTMENT, CREATE:/EDIT:/DELETE:<module>),
// each with 0-3 steps ("levels") and the roles allowed at each step.

interface Props {
  t: Tokens;
  notify: (message: string, type?: 'success' | 'error') => void;
}

const MAX_STEPS = 3;

/** How each kind of action is worded. */
const ACTION: Record<string, { icon: string; title: string; result: string; order: number }> = {
  REGISTRATION: { icon: '📝', title: 'New family registration (public link)', result: 'the family is added to Members & Families', order: 0 },
  BUDGET: { icon: '📊', title: 'New budget proposal', result: 'it goes into the Annual Budget Document', order: 0 },
  BUDGET_ADJUSTMENT: { icon: '🔁', title: 'New budget adjustment', result: 'it counts in Budget vs Actual', order: 1 },
  CREATE: { icon: '➕', title: 'Adding a new record', result: 'it is saved', order: 2 },
  EDIT: { icon: '✏️', title: 'Editing a record', result: 'the change is applied', order: 3 },
  DELETE: { icon: '🗑️', title: 'Deleting a record', result: 'it is deleted', order: 4 }
};

/** Parts where one screen action saves several records; each save then waits on its own. */
// Actions that save several records at once go for approval as ONE request
// and happen together on the last yes (the strictest rule among them applies).
const MULTI_SAVE: Record<string, string> = {
  'Members & Families': 'Promote to Head (old head, new head and family) and deleting a head with a new head go for approval as one request and happen together.',
  'Asset Management': 'A write-off (the written-off units and the reduced asset) and a transfer (with the new place of the asset) each go for approval as one request.',
  'Pastor Turnover': 'A new Pastor posting and archiving the current one go for approval as one request.',
  'Committee History': 'A new active term and closing the current one go for approval as one request.',
  'Sunday School': 'A new year (with archiving the old one) and a new grade with its sections each go for approval as one request.'
};

const kindOf = (type: string) => (type.includes(':') ? type.split(':')[0] : type);

const ApprovalsSection = ({ t, notify }: Props) => {
  const [policies, setPolicies] = useState<ApprovalPolicyView[]>([]);
  const [saved, setSaved] = useState<ApprovalPolicyView[]>([]);
  const [roles, setRoles] = useState<SystemRole[]>([]);
  const [part, setPart] = useState<string>('');
  const [onlyOn, setOnlyOn] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([approvalPolicies.list(), rolesApi.list()])
      .then(([p, r]) => {
        setPolicies(p || []);
        setSaved(p || []);
        setRoles(Array.isArray(r) ? r : []);
        if (p && p.length) setPart(p[0].module);
      })
      .catch((err) => notify((err as Error).message || 'Could not load the approval settings.', 'error'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Parts of the app in order, each with its actions. */
  const parts = useMemo(() => {
    const map = new Map<string, ApprovalPolicyView[]>();
    policies.forEach((p) => map.set(p.module, [...(map.get(p.module) || []), p]));
    map.forEach((list) => list.sort((a, b) => ACTION[kindOf(a.type)].order - ACTION[kindOf(b.type)].order));
    return Array.from(map.entries());
  }, [policies]);

  const isChanged = (p: ApprovalPolicyView) => {
    const s = saved.find((x) => x.type === p.type);
    return !s || s.levels !== p.levels || s.adminOverride !== p.adminOverride
      || JSON.stringify(s.levelRoles.slice(0, s.levels)) !== JSON.stringify(p.levelRoles.slice(0, p.levels));
  };

  const update = (type: string, patch: Partial<ApprovalPolicyView>) =>
    setPolicies((prev) => prev.map((p) => (p.type === type ? { ...p, ...patch } : p)));

  const setStepRoles = (p: ApprovalPolicyView, step: number, next: number[]) => {
    const levelRoles = Array.from({ length: MAX_STEPS }, (_, i) => [...(p.levelRoles[i] || [])]);
    levelRoles[step] = next;
    update(p.type, { levelRoles });
  };

  const removeLastStep = (p: ApprovalPolicyView) => {
    const levelRoles = Array.from({ length: MAX_STEPS }, (_, i) => [...(p.levelRoles[i] || [])]);
    levelRoles[p.levels - 1] = [];
    update(p.type, { levels: p.levels - 1, levelRoles });
  };

  const savePart = async (module: string) => {
    const changed = policies.filter((p) => p.module === module && isChanged(p));
    if (!changed.length) return;
    setSaving(true);
    try {
      let fresh: ApprovalPolicyView[] | null = null;
      for (const p of changed) {
        fresh = await approvalPolicies.save(p.type, { levels: p.levels, levelRoles: p.levelRoles, adminOverride: p.adminOverride });
      }
      if (fresh) { setPolicies(fresh); setSaved(fresh); }
      notify(`${module}: approval settings saved. They apply to new requests; ones already waiting keep their approvers.`, 'success');
    } catch (err) {
      notify((err as Error).message || 'Could not save.', 'error');
      // Some may have saved before the error: show what the server now holds.
      approvalPolicies.list().then((fresh) => { if (fresh) setSaved(fresh); }).catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  const discardPart = (module: string) =>
    setPolicies((prev) => prev.map((p) => (p.module === module ? (saved.find((s) => s.type === p.type) || p) : p)));

  const roleName = (id: number) => roles.find((r) => r.id === id)?.name || 'a removed role';
  const who = (p: ApprovalPolicyView, step: number) => {
    const ids = p.levelRoles[step] || [];
    return ids.length ? ids.map(roleName).join(' or ') : `anyone with Full Access to ${p.module} (not the person who asked)`;
  };
  const sentence = (p: ApprovalPolicyView) => {
    const a = ACTION[kindOf(p.type)];
    if (p.levels === 0) return `No approval needed — ${a.result} straight away.`;
    const steps = Array.from({ length: p.levels }, (_, i) => `${i + 1}. ${who(p, i)} approves`);
    return `${steps.join('  →  ')}  →  then ${a.result}.`;
  };

  const onIn = (list: ApprovalPolicyView[]) => list.filter((p) => p.levels > 0);
  const summary = (list: ApprovalPolicyView[]) =>
    onIn(list).map((p) => {
      const k = kindOf(p.type);
      return k === 'CREATE' ? 'Add' : k === 'EDIT' ? 'Edit' : k === 'DELETE' ? 'Delete'
        : k === 'REGISTRATION' ? 'Registration' : k === 'BUDGET' ? 'Proposal' : 'Adjustment';
    }).join(' · ');

  const visibleParts = parts.filter(([, list]) => !onlyOn || onIn(list).length > 0);
  const current = parts.find(([m]) => m === part);

  // textFaint is too dim on the dark card surfaces for text people need to read.
  const hint = t.isDarkMode ? t.textMuted : t.textFaint;

  // --- small building blocks ---------------------------------------------------------

  const toggle = (p: ApprovalPolicyView) => {
    const locked = p.minLevels > 0; // public registration / budget always need someone
    const on = p.levels > 0;
    return (
      <label
        title={locked ? (p.type === 'REGISTRATION'
          ? 'Anyone on the internet can send this form, so it always needs at least one approver.'
          : 'This always needs at least one approver.') : undefined}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: locked ? 'default' : 'pointer', userSelect: 'none' }}
      >
        <span style={{
          position: 'relative', width: '40px', height: '22px', borderRadius: '11px',
          backgroundColor: on ? t.primary : t.border, transition: 'background-color 0.15s', opacity: locked ? 0.7 : 1
        }}>
          <span style={{
            position: 'absolute', top: '3px', left: on ? '21px' : '3px', width: '16px', height: '16px',
            borderRadius: '50%', backgroundColor: '#FFF', transition: 'left 0.15s'
          }} />
        </span>
        <input
          type="checkbox" checked={on} disabled={locked} style={{ display: 'none' }}
          onChange={(e) => update(p.type, { levels: e.target.checked ? 1 : 0 })}
        />
        <strong style={{ color: on ? t.textMain : t.textMuted, fontSize: t.fontSize.sm }}>
          {on ? 'Needs approval' : 'Not needed'}
        </strong>
      </label>
    );
  };

  const stepRow = (p: ApprovalPolicyView, step: number) => {
    const chosen = p.levelRoles[step] || [];
    const addable = roles.filter((r) => r.active && !chosen.includes(r.id as number));
    return (
      <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '6px 0' }}>
        <span style={{ minWidth: '64px', fontWeight: 'bold', color: t.textMain, fontSize: t.fontSize.sm }}>
          Step {step + 1}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', flex: 1 }}>
          {chosen.length === 0 && (
            <span style={{ color: t.textMuted, fontSize: t.fontSize.sm, fontStyle: 'italic' }}>
              Anyone with Full Access to {p.module}, except the person who asked
            </span>
          )}
          {chosen.map((id, i) => (
            <React.Fragment key={id}>
              {i > 0 && <span style={{ color: hint, fontSize: t.fontSize.xs }}>or</span>}
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 10px',
                borderRadius: t.radius.pill, backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`,
                fontSize: t.fontSize.sm, color: t.textMain
              }}>
                {roleName(id)}
                <button
                  type="button" aria-label={`Remove ${roleName(id)}`}
                  onClick={() => setStepRoles(p, step, chosen.filter((x) => x !== id))}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: t.textMuted, padding: 0 }}
                >×</button>
              </span>
            </React.Fragment>
          ))}
          {addable.length > 0 && (
            <select
              value=""
              onChange={(e) => { if (e.target.value) setStepRoles(p, step, [...chosen, Number(e.target.value)]); }}
              style={{
                fontSize: t.fontSize.sm, padding: '3px 6px', borderRadius: t.radius.sm,
                border: `1px dashed ${t.borderStrong}`, backgroundColor: t.bgInput, color: t.primaryText, cursor: 'pointer'
              }}
            >
              <option value="">{chosen.length ? '+ or another role' : '+ Choose who approves'}</option>
              {addable.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
        </div>
        {step === p.levels - 1 && p.levels > Math.max(1, p.minLevels) && (
          <button
            type="button" onClick={() => removeLastStep(p)} title="Remove this step"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: t.textMuted, fontSize: t.fontSize.sm }}
          >✕ Remove step</button>
        )}
      </div>
    );
  };

  const actionCard = (p: ApprovalPolicyView) => {
    const a = ACTION[kindOf(p.type)];
    const on = p.levels > 0;
    return (
      <div key={p.type} style={{
        border: `1px solid ${on ? t.primary : t.border}`, borderRadius: t.radius.lg, padding: '12px 14px',
        marginBottom: '12px', backgroundColor: t.bgRaised
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <strong style={{ color: t.textMain, fontSize: t.fontSize.md }}>
            {a.icon} {a.title}{isChanged(p) && <span style={{ color: t.warning }} title="Not saved yet"> •</span>}
          </strong>
          {toggle(p)}
        </div>

        {on && (
          <div style={{ marginTop: '8px', borderTop: `1px dashed ${t.border}`, paddingTop: '4px' }}>
            {Array.from({ length: p.levels }, (_, i) => stepRow(p, i))}
            {p.levels < MAX_STEPS && (
              <button
                type="button"
                onClick={() => update(p.type, { levels: p.levels + 1 })}
                style={{ border: 'none', background: 'transparent', color: t.primaryText, cursor: 'pointer', padding: '4px 0', fontWeight: 'bold', fontSize: t.fontSize.sm }}
              >+ Add another step (a different person approves after)</button>
            )}
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px', color: t.textMuted, fontSize: t.fontSize.sm, cursor: 'pointer' }}>
              <input type="checkbox" checked={p.adminOverride} onChange={(e) => update(p.type, { adminOverride: e.target.checked })} />
              A System Administrator may approve any step in an emergency — including their own changes (shown in the history)
            </label>
          </div>
        )}

        <div style={{
          marginTop: '8px', padding: '6px 10px', borderRadius: t.radius.sm, backgroundColor: t.bgSubtle,
          color: on ? t.textMain : t.textMuted, fontSize: t.fontSize.sm
        }}>
          {sentence(p)}
        </div>

        {kindOf(p.type) === 'CREATE' && p.note && (
          <div style={{ color: hint, fontSize: t.fontSize.xs, marginTop: '6px' }}>{p.note}</div>
        )}
        {kindOf(p.type) === 'EDIT' && on && MULTI_SAVE[p.module] && (
          <div style={{ color: hint, fontSize: t.fontSize.xs, marginTop: '6px' }}>ℹ {MULTI_SAVE[p.module]}</div>
        )}
        {on && p.warnings.map((w) => (
          <div key={w} style={{ color: t.danger, fontSize: t.fontSize.sm, marginTop: '6px' }}>⚠ {w}</div>
        ))}
      </div>
    );
  };

  // --- page ----------------------------------------------------------------------------

  const partChanged = current ? current[1].some(isChanged) : false;

  return (
    <div>
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 6px', maxWidth: '900px' }}>
        Choose which actions must be <strong>approved before they happen</strong>, and who approves them.
        Pick a part of the app on the left.
      </p>
      <p style={{ color: hint, fontSize: t.fontSize.sm, margin: '0 0 16px', maxWidth: '900px' }}>
        Each step is a different person; nobody approves their own request. Approvers find waiting items under
        🔔 Approvals at the top of the screen. Roles are set up in Users &amp; Permissions › Roles.
      </p>

      <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Left: parts of the app */}
        <div style={{
          flex: '0 0 250px', maxWidth: '100%', border: `1px solid ${t.border}`, borderRadius: t.radius.lg,
          backgroundColor: t.bgRaised, overflow: 'hidden'
        }}>
          <label style={{ display: 'flex', gap: '6px', alignItems: 'center', padding: '8px 10px', fontSize: t.fontSize.sm, color: t.textMuted, borderBottom: `1px solid ${t.border}`, cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyOn} onChange={(e) => setOnlyOn(e.target.checked)} />
            Show only parts with approvals on
          </label>
          {visibleParts.map(([module, list]) => {
            const active = module === part;
            const s = summary(list);
            const dirty = list.some(isChanged);
            const warn = list.some((p) => p.levels > 0 && p.warnings.length > 0);
            return (
              <button
                key={module} type="button" onClick={() => setPart(module)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none',
                  borderLeft: `4px solid ${active ? t.primary : 'transparent'}`,
                  backgroundColor: active ? t.bgSubtle : 'transparent', cursor: 'pointer', color: t.textMain
                }}
              >
                <div style={{ fontWeight: active ? 'bold' : 'normal', fontSize: t.fontSize.sm }}>
                  {module}{dirty && <span style={{ color: t.warning }}> •</span>}{warn && <span style={{ color: t.danger }}> ⚠</span>}
                </div>
                <div style={{ fontSize: t.fontSize.xs, color: s ? t.primaryText : hint }}>{s || 'No approvals'}</div>
              </button>
            );
          })}
          {!visibleParts.length && (
            <div style={{ padding: '10px 12px', color: hint, fontSize: t.fontSize.sm }}>No part has approvals on yet.</div>
          )}
        </div>

        {/* Right: the chosen part */}
        <div style={{ flex: '1 1 480px', minWidth: 0 }}>
          {current && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, color: t.textMain, fontSize: t.fontSize.lg }}>{current[0]}</h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {partChanged && (
                    <Button size="sm" variant="ghost" disabled={saving} onClick={() => discardPart(current[0])}>Undo changes</Button>
                  )}
                  <Button size="sm" disabled={saving || !partChanged} onClick={() => savePart(current[0])}>
                    {saving ? 'Saving…' : `💾 Save ${current[0]}`}
                  </Button>
                </div>
              </div>
              {current[1].map(actionCard)}
              {partChanged && (
                <p style={{ color: t.warning, fontSize: t.fontSize.sm, margin: '4px 0 0' }}>
                  • You have changes here that are not saved yet.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalsSection;
